import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
    Card, CardBody, Input, Col, Row,
    NavItem, NavLink, TabContent, TabPane
} from "reactstrap";
import classnames from "classnames";
import { v4 as uuidv4 } from 'uuid';
import PlantSelect from "../PlantList/PlantSelect";
import PlantPreviewCard from "../PlantList/PlantPreviewCard";
import { StorageImage } from "@aws-amplify/ui-react-storage";
import { jsPDF } from "jspdf";
import { getPlantData, getClimateData } from "../../store/design/actions";
import { getProfile } from "../../store/auth/profile/actions";
const Spline = require("cubic-spline");

/* =====================================================
   Crop Planner
===================================================== */
const CropPlanner = ({
    plantData, plantMap, climateData, profile,
    onGetPlantData, onGetProfile, onGetClimateData,
    areas, currYear, onAddRotationToArea
}) => {
    const username = JSON.parse(localStorage.getItem("user")).user;

    /* Wizard state */
    const [activeTabProgress, setActiveTabProgress] = useState(1);
    const [progressValue, setProgressValue] = useState(5);
    const [saved, setSaved] = useState(false);

    /* Step 1 */
    const [selectedAreaId, setSelectedAreaId] = useState("");
    const [plants, setPlants] = useState([]);
    const [plantOptions, setPlantOptions] = useState([]);
    const [pendingCrop, setPendingCrop] = useState(null);
    const [searchKey, setSearchKey] = useState(0);
    const [errors, setErrors] = useState({});

    /* Step 2 */
    const [rotations, setRotations] = useState([]);

    /* Single-season mode */
    const [planMode, setPlanMode] = useState("multi");   // "multi" | "single"
    const [season, setSeason] = useState("Spring");
    const [successionWeeks, setSuccessionWeeks] = useState("2");   // "2" | "3" | "4" | "consecutive"
    const [ssTab, setSsTab] = useState(1);
    const [ssPlanned, setSsPlanned] = useState([]);
    const [ssSaved, setSsSaved] = useState(false);
    const [ssErrors, setSsErrors] = useState({});

    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

    /* ---- Load data ---- */
    useEffect(() => {
        onGetProfile(username);
        onGetPlantData();
    }, []);

    useEffect(() => {
        if (profile?.address?.zipcode) {
            onGetClimateData(profile.address.zipcode);
        }
    }, [profile?.address?.zipcode]);

    useEffect(() => {
        setPlantOptions(
            plantData
                .filter(p => p.season !== "perennial")
                .map(p => ({ key: p.name, label: p.name }))
        );
    }, [plantData]);

    /* ---- Helpers ---- */
    function formatDate(d) {
        if (!d) return "—";
        const dt = new Date(d);
        return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }

    function getWeatherType(plant) {
        if (!plant?.minimumTemp || !plant?.maximumTemp) return null;
        const min = parseFloat(plant.minimumTemp);
        const max = parseFloat(plant.maximumTemp);
        return (min + max) / 2 >= 70 ? "warm" : "cool";
    }

    /* ---- Plant CRUD ---- */
    function addPlantFromSearch(speciesName) {
        const species = typeof speciesName === "string" ? speciesName : speciesName?.key;
        if (!species || !plantMap[species]) return;
        setPendingCrop({ species, ...plantMap[species] });
    }

    function confirmAddCrop() {
        if (!pendingCrop) return;
        setPlants(prev => [...prev, pendingCrop]);
        setPendingCrop(null);
        setSearchKey(k => k + 1);
        setErrors(prev => ({ ...prev, plants: undefined }));
    }

    function removePlant(index) {
        setPlants(plants.filter((_, i) => i !== index));
    }

    /* ---- Single-season planning ---- */
    // Last 0-indexed month of each season (matches SEASON_MONTHS' last entries).
    const SEASON_LAST_MONTH = { Spring: 4, Summer: 8, Fall: 11 };

    function seasonEndDate(seas) {
        const lastMonth = SEASON_LAST_MONTH[seas] ?? 11;
        return new Date(new Date().getFullYear(), lastMonth + 1, 0); // last day of that month
    }

    // End of the crop's suitable planting window. Mirrors plants-areas'
    // calcPlantingCalendar: the window runs to the end of the contiguous suitable
    // temperature range whose start falls in the season — which may extend PAST the
    // season's last month (so succession counts match the Add-Plant flow).
    function cropWindowEnd(plant) {
        const min = parseFloat(plant.minimumTemp), max = parseFloat(plant.maximumTemp);
        let climateInfo = climateData;
        if (profile?.climates?.length) { const sel = profile.climates.find(c => c.selected); if (sel) climateInfo = sel; }
        const seasonMonths = SEASON_MONTHS[season] || [];
        const yr = new Date().getFullYear();

        // Every month whose climate suits this crop (0-11).
        const plWindow = [];
        for (let i = 0; i < 12; i++) {
            if (parseFloat(climateInfo[months[i] + "Min"]) >= min && parseFloat(climateInfo[months[i] + "Max"]) <= max)
                plWindow.push(i);
        }
        if (plWindow.length === 0) return null;

        // Group into contiguous month ranges (with Dec→Jan wrap).
        const ranges = [];
        let start = plWindow[0], pointer = start;
        for (let i = 1; i < plWindow.length; i++) {
            const cur = plWindow[i];
            if (cur === pointer + 1 || (pointer === 11 && cur === 0)) { pointer = cur; }
            else { ranges.push({ start, end: pointer }); start = cur; pointer = cur; }
        }
        ranges.push({ start, end: pointer });

        // Clamp each range's start into the season; keep its natural (possibly later) end.
        const adjusted = ranges
            .map(r => {
                const adj = seasonMonths.includes(r.start) ? r.start : (seasonMonths.find(m => m >= r.start) ?? null);
                return (adj != null && seasonMonths.includes(adj)) ? { start: adj, end: r.end } : null;
            })
            .filter(Boolean);
        if (adjusted.length === 0) return null;

        // Exact last suitable day of the range's end month (interpolated — can run a
        // few days into the next month), matching plants-areas' window end. Fall back
        // to the last calendar day of that month if the spline can't resolve a day.
        const endMonth = adjusted[0].end;
        const endDay = calcExactDay(endMonth, min, max, climateInfo, yr, true);
        return endDay || new Date(yr, endMonth + 1, 0);
    }

    // Returns one group per crop: { crop, family, entries:[plant,...] }.
    // Succession plantings start today (or the window open, whichever is later) and
    // repeat at the chosen interval through the end of the crop's planting window.
    function buildSingleSeasonPlan() {
        const groups = [];
        const today = new Date(); today.setHours(0, 0, 0, 0);
        plants.forEach(p => {
            const base = recalcPlantDatesForSeason(p, season);
            if (!base.seedStartDate) return; // no suitable window for this crop/season
            const dth = parseInt(p.daysToHarvest || 0);
            const germ = parseInt(p.germinationTime || 0);
            const dtt = parseInt(p.daysToTransplant || 0);
            const windowEnd = cropWindowEnd(p) || seasonEndDate(season);
            const naturalSeed = new Date(base.seedStartDate);

            // Build a planting from a SEED date so the start is never in the past.
            const makeEntry = (seed) => {
                const s = new Date(seed);
                const t = new Date(s); t.setDate(t.getDate() + germ + dtt);   // transplant
                const harvest = new Date(t); harvest.setDate(harvest.getDate() + dth);
                return buildPlantForArea({ ...p, season_rotation: season, seedStartDate: s, transplantDate: t, harvestDate: harvest }, profile);
            };

            // First seed = today at the earliest (never the past), but not before the window opens.
            const firstSeed = new Date(Math.max(today.getTime(), naturalSeed.getTime()));

            const entries = [];
            if (p.succession && dth > 0 && dth < 65) {
                const consecutive = successionWeeks === "consecutive";
                const stepDays = (parseInt(successionWeeks) || 2) * 7;
                let seed = new Date(firstSeed);
                for (let i = 0; i < 26; i++) {  // defensive cap
                    const harvest = new Date(seed); harvest.setDate(harvest.getDate() + germ + dtt + dth);
                    if (harvest > windowEnd) break;   // can't finish before the window closes → stop
                    entries.push(makeEntry(seed));
                    if (consecutive) {
                        // next transplant = 1 week after this harvest → seed it (germ+dtt) earlier
                        seed = new Date(harvest); seed.setDate(seed.getDate() + 7 - (germ + dtt));
                    } else {
                        seed = new Date(seed); seed.setDate(seed.getDate() + stepDays);
                    }
                }
                if (entries.length === 0) entries.push(makeEntry(firstSeed)); // at least one
            } else {
                entries.push(makeEntry(firstSeed));
            }
            // TEMP DIAGNOSTIC — remove once succession matches plants-areas
            console.warn("[CP succession]", p.species, {
                season, dth, germ, dtt, weeks: successionWeeks,
                naturalSeed: naturalSeed.toDateString(),
                firstSeed: firstSeed.toDateString(),
                windowEnd: windowEnd.toDateString(),
                count: entries.length,
                seeds: entries.map(e => e.seedStartDate),
            });
            groups.push({ crop: p, family: p.family || p.plant_family || "Unknown", entries });
        });
        return groups;
    }

    function ssToggleSuccession(index) {
        setPlants(prev => prev.map((p, i) => i === index ? { ...p, succession: !p.succession } : p));
    }

    function ssSetNumPlants(index, val) {
        setPlants(prev => prev.map((p, i) => i === index ? { ...p, numPlants: val } : p));
    }

    function ssGoNext() {
        if (ssTab === 1) {
            const errs = {};
            if (!selectedAreaId) errs.area = "Please select an area.";
            if (plants.length === 0) errs.plants = "Please add at least one crop.";
            setSsErrors(errs);
            if (Object.keys(errs).length > 0) return;
            const planned = buildSingleSeasonPlan();
            if (planned.length === 0) { setSsErrors({ plants: "None of these crops have a suitable window in this season." }); return; }
            setSsPlanned(planned);
            setSsTab(2);
        } else if (ssTab === 2) {
            setSsTab(3);
        }
    }

    function ssSaveAll() {
        const all = ssPlanned.flatMap(g => g.entries);
        if (!all.length) return;
        onAddRotationToArea(selectedAreaId, all.map(normalizePlantDates));
        setSsSaved(true);
    }

    function ssReset() {
        setSsSaved(false); setSsTab(1); setSelectedAreaId(""); setPlants([]);
        setPendingCrop(null); setSearchKey(k => k + 1); setSsPlanned([]); setSsErrors({});
    }

    function printPlanTimeline() {
        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        const pageW = 297, margin = 15, cw = pageW - margin * 2;
        const nameW = cw * 0.28, barW = cw * 0.72, barX = margin + nameW;
        const rowH = 7, monthW = barW / 12;
        const yr = new Date().getFullYear();
        const yearStart = new Date(yr, 0, 1);
        const totalDays = (new Date(yr + 1, 0, 1) - yearStart) / 86400000;
        const MS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
        const pctOf = (d) => Math.max(0, Math.min(1, (new Date(d) - yearStart) / 86400000 / totalDays));
        const flat = ssPlanned.flatMap(g => g.entries);

        doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(47, 58, 52);
        doc.text(`${season} Plan — ${selectedArea?.name || ""} (${yr})`, margin, margin + 5);
        let y = margin + 13;
        doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(150, 150, 150);
        MS.forEach((m, i) => doc.text(m, barX + i * monthW + monthW / 2, y, { align: "center" }));
        y += 4;
        const startY = y;

        flat.forEach((p, idx) => {
            const rowY = startY + idx * rowH;
            if (idx % 2 === 0) { doc.setFillColor(248, 251, 248); doc.rect(margin, rowY, cw, rowH, "F"); }
            doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(47, 58, 52);
            doc.text(`${p.species}${p.variety ? ` (${p.variety})` : ""}`, margin, rowY + rowH * 0.68, { maxWidth: nameW - 2 });
            doc.setFillColor(224, 224, 224); doc.rect(barX, rowY + rowH * 0.28, barW, rowH * 0.44, "F");
            const sp = pctOf(p.seedStartDate), mp = pctOf(p.transplantDate), ep = pctOf(p.harvestDate);
            doc.setFillColor(0, 184, 148); doc.rect(barX + sp * barW, rowY + rowH * 0.28, (mp - sp) * barW, rowH * 0.44, "F");
            doc.setFillColor(9, 132, 227); doc.rect(barX + mp * barW, rowY + rowH * 0.28, (ep - mp) * barW, rowH * 0.44, "F");
        });

        const gridH = flat.length * rowH;
        doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.2);
        for (let i = 0; i <= 12; i++) { const x = barX + i * monthW; doc.line(x, startY, x, startY + gridH); }
        const tx = barX + pctOf(new Date()) * barW;
        doc.setDrawColor(255, 69, 96); doc.setLineWidth(0.5); doc.line(tx, startY, tx, startY + gridH); doc.setLineWidth(0.2);

        const legY = startY + gridH + 7;
        doc.setFontSize(7); doc.setTextColor(100, 100, 100);
        doc.setFillColor(0, 184, 148); doc.rect(margin, legY, 7, 3.5, "F"); doc.text("Seed → Transplant", margin + 9, legY + 3);
        doc.setFillColor(9, 132, 227); doc.rect(margin + 52, legY, 7, 3.5, "F"); doc.text("Transplant → Harvest", margin + 61, legY + 3);
        doc.setDrawColor(255, 69, 96); doc.setLineWidth(0.6); doc.line(margin + 118, legY + 1.75, margin + 125, legY + 1.75); doc.setLineWidth(0.2); doc.text("Today", margin + 127, legY + 3);

        doc.save(`${season.toLowerCase()}-plan-${yr}.pdf`);
    }

    /* ---- Date math ---- */
    function calcExactDay(keyMonth, min, max, climateInfo, baseYear, inv = false) {
        const xs = [0, 30, 60];
        const prevMonth = (keyMonth + 11) % 12;
        const nextMonth = (keyMonth + 1) % 12;

        const ymins = [
            parseFloat(climateInfo[months[prevMonth] + "Min"]),
            parseFloat(climateInfo[months[keyMonth] + "Min"]),
            parseFloat(climateInfo[months[nextMonth] + "Min"])
        ];
        const ymaxs = [
            parseFloat(climateInfo[months[prevMonth] + "Max"]),
            parseFloat(climateInfo[months[keyMonth] + "Max"]),
            parseFloat(climateInfo[months[nextMonth] + "Max"])
        ];

        const minSpline = new Spline(xs, ymins);
        const maxSpline = new Spline(xs, ymaxs);

        let keyDate = null;
        if (!inv) {
            for (let i = 0; i <= 60; i++) {
                if (minSpline.at(i) >= min && maxSpline.at(i) <= max) { keyDate = i; break; }
            }
        } else {
            // Last suitable day (window end) — scan backward, matches plants-areas.
            for (let i = 60; i >= 0; i--) {
                if (minSpline.at(i) >= min && maxSpline.at(i) <= max) { keyDate = i; break; }
            }
        }
        if (keyDate == null) return null;

        const midPrev = new Date(baseYear, prevMonth, 15);
        const d = new Date(midPrev);
        d.setDate(d.getDate() + keyDate);
        return d;
    }

    const SEASON_MONTHS = {
        Spring: [1,2,3,4],
        Summer: [5,6,7,8],
        Fall:   [9,10,11]
    };

    function recalcPlantDatesForSeason(plant, season) {
        if (!plant || !climateData) return plant;

        let climateInfo = climateData;
        if (profile?.climates?.length) {
            const selected = profile.climates.find(c => c.selected);
            if (selected) climateInfo = selected;
        }

        const monthsToTry = SEASON_MONTHS[season];
        if (!monthsToTry) return plant;

        const min = parseFloat(plant.minimumTemp);
        const max = parseFloat(plant.maximumTemp);
        const baseYear = new Date().getFullYear();

        let startDate = null;
        for (let m of monthsToTry) {
            startDate = calcExactDay(m, min, max, climateInfo, baseYear);
            if (startDate) break;
        }
        if (!startDate) return plant;

        const daysToTransplant = parseInt(plant.daysToTransplant || 0);
        const germination = parseInt(plant.germinationTime || 0);
        const daysToHarvest = parseInt(plant.daysToHarvest || 0);

        const seedDate = new Date(startDate);
        seedDate.setDate(seedDate.getDate() - daysToTransplant - germination);

        const transplantDate = new Date(startDate);
        const harvestDate = new Date(startDate);
        harvestDate.setDate(harvestDate.getDate() + daysToHarvest);

        return {
            ...plant,
            season_rotation: season,
            seedStartDate: seedDate,
            transplantDate,
            harvestDate
        };
    }

    /* ---- Crop rotation logic ---- */
    function generateCropRotations(plantList) {
        const remaining = [...plantList];
        const result = [];

        const nutrientRank = { heavy_giver: 0, light_feeder: 1, heavy_feeder: 2 };

        function pickBest(candidates, prevPlant, families) {
            let best = null, bestScore = -Infinity;
            for (const p of candidates) {
                let score = 0;
                if (prevPlant) {
                    const prev = nutrientRank[prevPlant.nutrient_role] ?? 1;
                    const curr = nutrientRank[p.nutrient_role] ?? 1;
                    if (curr >= prev) score += 5;
                } else score += 2;
                if (p.family && !families.has(p.family)) score += 1;
                if (p.daysToHarvest) score += Math.max(0, 120 - p.daysToHarvest) / 100;
                if (score > bestScore) { best = p; bestScore = score; }
            }
            return best;
        }

        while (remaining.length) {
            const rotation = { spring: null, summer: null, fall: null };
            const families = new Set();

            const cool = remaining.filter(p => getWeatherType(p) === "cool");
            const warm = remaining.filter(p => getWeatherType(p) === "warm");

            const spring = pickBest(cool, null, families);
            if (spring) { rotation.spring = spring; families.add(spring.family); }
            const summer = pickBest(warm, spring, families);
            if (summer) { rotation.summer = summer; families.add(summer.family); }
            const fall = pickBest(cool.filter(p => p !== spring), summer || spring, families);
            if (fall) { rotation.fall = fall; families.add(fall.family); }

            if (!spring && !summer && !fall) break;

            [spring, summer, fall].filter(Boolean).forEach(p => {
                const idx = remaining.findIndex(r => r.species === p.species);
                if (idx >= 0) remaining.splice(idx, 1);
            });

            result.push(rotation);
        }
        return result;
    }

    /* ---- Build plant for area ---- */
    function buildPlantForArea(plant, prof) {
        const numPlants = parseInt(plant.numPlants) || 1;
        // Spread the full template first so Designer/card fields (size, designerIconKey,
        // cardIconKey, temps, growth days, etc.) carry through — then override the
        // computed/reset fields below. Previously only a subset was copied, which
        // dropped `size` and made plants place at the wrong scale in the Designer.
        const newPlant = {
            ...plant,
            id: uuidv4(),
            species: plant.species,
            variety: plant.variety || "",
            family: plant.family,
            season: plant.season,
            season_rotation: plant.season_rotation,
            seedStartDate: plant.seedStartDate,
            transplantDate: plant.transplantDate,
            harvestDate: plant.harvestDate,
            status: "Seed Starting",
            numPlants,
            harvests: [],
            notes: [],
            totalHarvest: 0,
            harvestTotals: 0,
            spacing_between_plants: plant.spacing_between_plants,
            yield_per_plant_ounces: plant.yield_per_plant_ounces
        };
        delete newPlant.succession;   // crop-planner UI flag, not a saved plant field

        const numPerSqFt = Math.pow(12 / plant.spacing_between_plants, 2);
        newPlant.spaceUsed = (numPlants / numPerSqFt).toFixed(2);
        newPlant.estHarvest = plant.yield_per_plant_ounces
            ? (plant.yield_per_plant_ounces * numPlants) / 16.0
            : 0;

        if (prof?.unitmeasurement === "metric") {
            newPlant.spaceUsed = (newPlant.spaceUsed / 10.764).toFixed(2);
            newPlant.estHarvest = (newPlant.estHarvest / 2.2).toFixed(2);
        }
        return newPlant;
    }

    function normalizePlantDates(plant) {
        return {
            ...plant,
            seedStartDate: plant.seedStartDate ? new Date(plant.seedStartDate).toISOString() : "",
            transplantDate: plant.transplantDate ? new Date(plant.transplantDate).toISOString() : "",
            harvestDate: plant.harvestDate ? new Date(plant.harvestDate).toISOString() : ""
        };
    }

    /* ---- Wizard navigation ---- */
    function toggleTabProgress(tab) {
        if (activeTabProgress !== tab) {
            const vals = { 1: 5, 2: 50, 3: 95 };
            setProgressValue(vals[tab] || 5);
            setActiveTabProgress(tab);
        }
    }

    function goNext() {
        if (activeTabProgress === 1) {
            const errs = {};
            if (!selectedAreaId) errs.area = "Please select an area.";
            if (plants.length === 0) errs.plants = "Please add at least one crop.";
            setErrors(errs);
            if (Object.keys(errs).length > 0) return;

            const raw = generateCropRotations(plants);
            setRotations(raw.map(r => ({
                spring: r.spring ? recalcPlantDatesForSeason(r.spring, "Spring") : null,
                summer: r.summer ? recalcPlantDatesForSeason(r.summer, "Summer") : null,
                fall:   r.fall   ? recalcPlantDatesForSeason(r.fall,   "Fall")   : null
            })));
            toggleTabProgress(2);
        } else if (activeTabProgress === 2) {
            toggleTabProgress(3);
        }
    }

    function handleSaveAll() {
        const allPlants = [];
        rotations.forEach(rotation => {
            ["spring", "summer", "fall"].forEach(season => {
                if (rotation[season]) {
                    allPlants.push(normalizePlantDates(buildPlantForArea(rotation[season], profile)));
                }
            });
        });
        if (allPlants.length === 0) return;
        onAddRotationToArea(selectedAreaId, allPlants);
        setSaved(true);
    }

    function resetWizard() {
        setSaved(false);
        setActiveTabProgress(1);
        setProgressValue(5);
        setSelectedAreaId("");
        setPlants([]);
        setPendingCrop(null);
        setSearchKey(0);
        setErrors({});
        setRotations([]);
    }

    const selectedArea = areas.find(a => a.id === selectedAreaId);
    const totalCropCount = rotations.reduce(
        (sum, r) => sum + ["spring","summer","fall"].filter(s => r[s]).length, 0
    );

    /* ---- Mode toggle (shared by both planners) ---- */
    const modeToggle = (
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {[{ k: "multi", label: "Multi-season rotation" }, { k: "single", label: "Single season" }].map(({ k, label }) => (
                <button key={k} type="button" className="btn btn-sm"
                    onClick={() => setPlanMode(k)}
                    style={planMode === k
                        ? { backgroundColor: "#7F8F7B", borderColor: "#7F8F7B", color: "#fff" }
                        : { backgroundColor: "#fff", borderColor: "#d1d5db", color: "#212529" }}>
                    {label}
                </button>
            ))}
        </div>
    );

    /* ============ SINGLE-SEASON PLANNER ============ */
    if (planMode === "single") {
        // Families counted by chosen crop (with plant_family fallback); space across all plantings.
        const famCounts = {};
        plants.forEach(p => { const f = p.family || p.plant_family || "Unknown"; famCounts[f] = (famCounts[f] || 0) + 1; });
        const famEntries = Object.entries(famCounts).sort((a, b) => b[1] - a[1]);
        const totalPlantings = ssPlanned.reduce((s, g) => s + g.entries.length, 0);
        // Successions reuse the same bed over the season, so count each crop's footprint once.
        const totalSpaceUsed = ssPlanned.reduce((s, g) => s + (parseFloat(g.entries[0]?.spaceUsed) || 0), 0);
        // Each succession planting yields its own harvest, so sum across every planting.
        const totalEstHarvest = ssPlanned.reduce((s, g) => s + g.entries.reduce((es, e) => es + (parseFloat(e.estHarvest) || 0), 0), 0);
        const harvestUnit = profile?.unitmeasurement === "metric" ? "kg" : "lb";
        const available = parseFloat(selectedArea?.info?.totalSpace || 0);
        const overSpace = available > 0 && totalSpaceUsed > available;
        const dominantFamily = famEntries.find(([, c]) => plants.length >= 3 && c / plants.length > 0.5);
        const spaceUnit = profile?.unitmeasurement === "metric" ? "m²" : "ft²";

        // Family diversity score (same model as the guild builder: families minus repeats).
        const numFamilies = famEntries.length;
        const familyPenalty = famEntries.reduce((s, [, c]) => s + Math.max(0, c - 1), 0);
        const diversityScore = numFamilies - familyPenalty;
        const diversityRating =
            diversityScore >= 5 ? { label: "Great", color: "#7F8F7B" } :
            diversityScore === 4 ? { label: "Good", color: "#8FAA6F" } :
            diversityScore === 3 ? { label: "Ok", color: "#FF9900" } :
            diversityScore === 2 ? { label: "Fair", color: "#E07B39" } :
                                   { label: "Poor", color: "#C0392B" };

        // Year-relative position helper for the per-crop mini timeline.
        const yr2 = new Date().getFullYear();
        const yStart = new Date(yr2, 0, 1).getTime();
        const yTot = new Date(yr2 + 1, 0, 1).getTime() - yStart;
        const pct = (d) => Math.max(0, Math.min(100, (new Date(d).getTime() - yStart) / yTot * 100));

        return (
            <React.Fragment>
            <Row><Col lg="12"><Card><CardBody>
                {modeToggle}

                {ssSaved ? (
                    <div style={{ textAlign: "center", padding: "40px 20px 24px" }}>
                        <div style={{ width: 72, height: 72, borderRadius: "50%", backgroundColor: "#DDE5D7", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                            <i className="fas fa-check" style={{ fontSize: 32, color: "#4a5e44" }} />
                        </div>
                        <h4 style={{ color: "#2e3d2a", marginBottom: 6 }}>Plan Saved!</h4>
                        <p className="text-muted mb-4">
                            {totalPlantings} planting{totalPlantings === 1 ? "" : "s"} added to <strong>{selectedArea?.name}</strong> for {season}.
                        </p>
                        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
                            <button type="button" className="btn btn-sm" style={{ backgroundColor: "#fff", borderColor: "#d1d5db", color: "#212529" }} onClick={printPlanTimeline}>
                                <i className="bx bx-printer" /> Print Timeline
                            </button>
                            <button type="button" className="btn btn-sm" style={{ backgroundColor: "#FF9900", borderColor: "#FF9900", color: "#fff" }} onClick={ssReset}>
                                Plan Another Season
                            </button>
                        </div>
                    </div>
                ) : (<>
                    {/* Tab bar — same look as the multi-season wizard */}
                    <style>{`
                        .twitter-bs-wizard-nav .nav-link .step-number {
                            background-color: #BEC8B6 !important; color: #3d4a38 !important; border-color: #BEC8B6 !important;
                        }
                        .twitter-bs-wizard-nav .nav-link.active .step-number {
                            background-color: #FF9900 !important; color: #fff !important; border-color: #FF9900 !important;
                        }
                        .twitter-bs-wizard-nav .nav-link.active { color: #212529 !important; }
                        .twitter-bs-wizard-nav,
                        .twitter-bs-wizard-nav .nav-link,
                        .twitter-bs-wizard-nav .nav-link.active { background-color: #DDE5D7 !important; }
                        .twitter-bs-wizard-nav .nav-link.completed .step-number {
                            background-color: #7F8F7B !important; color: #fff !important; border-color: #7F8F7B !important;
                        }
                    `}</style>
                    <div id="progrss-wizard-ss" className="twitter-bs-wizard" style={{ marginBottom: 20 }}>
                        <ul className="twitter-bs-wizard-nav nav-justified nav nav-pills">
                            {[{ n: 1, label: "Choose Area & Crops" }, { n: 2, label: "Review Plan" }, { n: 3, label: "Save Plan" }].map(({ n, label }) => (
                                <NavItem key={n}>
                                    <NavLink
                                        className={classnames({ active: ssTab === n, completed: ssTab > n })}
                                        onClick={() => ssTab > n && setSsTab(n)}
                                        style={{ cursor: ssTab > n ? "pointer" : "default" }}>
                                        <span className="step-number mr-2">{ssTab > n ? <i className="fas fa-check" /> : n}</span>
                                        <span>{label}</span>
                                    </NavLink>
                                </NavItem>
                            ))}
                        </ul>
                    </div>

                    {/* ---- Tab 1: Choose Area & Crops ---- */}
                    {ssTab === 1 && (
                        <div>
                            <Row className="mb-3">
                                <Col md={4}><b>Area</b>
                                    <Input type="select" value={selectedAreaId} onChange={e => setSelectedAreaId(e.target.value)}>
                                        <option value="">Select area…</option>
                                        {areas.filter(a => a.name !== "Unassigned" && a.year === currYear).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                    </Input>
                                    <span style={{ color: "#FF0000", fontSize: "0.8rem" }}>{ssErrors.area}</span>
                                </Col>
                                <Col md={4}><b>Season</b>
                                    <Input type="select" value={season} onChange={e => setSeason(e.target.value)}>
                                        <option>Spring</option><option>Summer</option><option>Fall</option>
                                    </Input>
                                </Col>
                                <Col md={4}><b>Succession interval</b>
                                    <Input type="select" value={successionWeeks} onChange={e => setSuccessionWeeks(e.target.value)}>
                                        <option value="2">Every 2 weeks</option>
                                        <option value="3">Every 3 weeks</option>
                                        <option value="4">Every 4 weeks</option>
                                        <option value="consecutive">Consecutive (after harvest)</option>
                                    </Input>
                                </Col>
                            </Row>

                            <Row className="mb-2">
                                <Col md={8}><b>Add crop</b>
                                    <PlantSelect key={searchKey} plantOptions={plantOptions} value="" placeholder="Search crops…" onChange={(val) => addPlantFromSearch(val)} />
                                </Col>
                                <Col md={4} className="d-flex align-items-end">
                                    {pendingCrop && (
                                        <button type="button" className="btn btn-sm" style={{ backgroundColor: "#FF9900", borderColor: "#FF9900", color: "#fff" }} onClick={confirmAddCrop}>
                                            <i className="bx bx-plus" /> Add {pendingCrop.species}
                                        </button>
                                    )}
                                </Col>
                            </Row>
                            <span style={{ color: "#FF0000", fontSize: "0.8rem" }}>{ssErrors.plants}</span>

                            {/* Crop list */}
                            <div style={{ marginTop: 12 }}>
                                {plants
                                    .map((p, i) => ({ p, i }))
                                    .sort((a, b) => (parseInt(a.p.daysToHarvest) || 0) - (parseInt(b.p.daysToHarvest) || 0))
                                    .map(({ p, i }) => {
                                    const dth = parseInt(p.daysToHarvest || 0);
                                    return (
                                        <div key={i} style={{ display: "flex", flexWrap: "nowrap", alignItems: "center", gap: 12, padding: "8px 10px", borderBottom: "1px solid #eee" }}>
                                            <div style={{ flexShrink: 0, width: 48, height: 40, borderRadius: 4, overflow: "hidden", border: "1px solid #dde5d7", display: "flex", alignItems: "center", backgroundColor: "#f5f6f3" }}>
                                                <StorageImage
                                                    alt={p.species}
                                                    imgKey={p.cardIconKey
                                                        ? p.cardIconKey
                                                        : "card_icons/" + p.species.toLowerCase().replace(/ /g, "_").replace(/[(),]/g, "") + ".png"}
                                                    accessLevel="guest"
                                                    onStorageGetError={() => {}}
                                                    style={{ width: "48px", height: "40px", objectFit: "cover", display: "block" }}
                                                />
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <b>{p.species}</b>
                                                <span style={{ color: "#6c757d", fontSize: "0.82rem" }}>
                                                    {p.family ? ` · ${p.family}` : ""}{dth ? ` · ${dth} days to harvest` : ""}
                                                </span>
                                            </div>
                                            <label style={{ width: 120, flexShrink: 0, margin: 0, fontSize: "0.82rem", color: "#2e3d2a", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 5, whiteSpace: "nowrap" }}>
                                                # plants
                                                <Input type="number" min="1" value={p.numPlants ?? 1} onChange={e => ssSetNumPlants(i, e.target.value)} style={{ width: 56, marginTop: 0 }} />
                                            </label>
                                            <div style={{ width: 110, flexShrink: 0, marginLeft: 16 }}>
                                                {dth > 0 && dth < 65 && (
                                                    <label style={{ margin: 0, fontSize: "0.82rem", color: "#2e3d2a", display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>
                                                        <Input type="checkbox" checked={!!p.succession} onChange={() => ssToggleSuccession(i)} style={{ marginTop: 0 }} />
                                                        Succession
                                                    </label>
                                                )}
                                            </div>
                                            <i className="fas fa-trash" style={{ cursor: "pointer", color: "#B93535", flexShrink: 0 }} onClick={() => removePlant(i)} />
                                        </div>
                                    );
                                })}
                            </div>

                            <div style={{ marginTop: 20, textAlign: "right" }}>
                                <button type="button" className="btn btn-sm" style={{ backgroundColor: "#FF9900", borderColor: "#FF9900", color: "#fff" }} onClick={ssGoNext}>
                                    Review Plan <i className="bx bx-right-arrow-alt" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ---- Tab 2: Review Plan ---- */}
                    {ssTab === 2 && (
                        <div>
                            {/* Guild-style summary stats */}
                            <div style={{ border: "1px solid #dde5d7", borderRadius: 8, overflow: "hidden", marginBottom: 16 }}>
                                <div style={{ display: "flex", flexWrap: "wrap", backgroundColor: "#f5f8f3", padding: "14px 20px", gap: 28 }}>
                                    <div style={{ textAlign: "center" }}>
                                        <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#2e3d2a", lineHeight: 1 }}>{plants.length}</div>
                                        <div style={{ fontSize: "0.7rem", color: "#6c757d", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>Crops</div>
                                    </div>
                                    <div style={{ textAlign: "center" }}>
                                        <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#2e3d2a", lineHeight: 1 }}>{totalPlantings}</div>
                                        <div style={{ fontSize: "0.7rem", color: "#6c757d", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>Plantings</div>
                                    </div>
                                    <div style={{ textAlign: "center" }}>
                                        <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#2e3d2a", lineHeight: 1 }}>{numFamilies}</div>
                                        <div style={{ fontSize: "0.7rem", color: "#6c757d", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>Families</div>
                                    </div>
                                    <div style={{ textAlign: "center" }}>
                                        <div style={{ fontSize: "1.1rem", fontWeight: 700, color: diversityRating.color, lineHeight: 1.2 }}>{diversityRating.label}</div>
                                        <div style={{ fontSize: "0.7rem", color: "#6c757d", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>Diversity</div>
                                    </div>
                                    <div style={{ textAlign: "center" }}>
                                        <div style={{ fontSize: "1.5rem", fontWeight: 700, color: overSpace ? "#C0392B" : "#2e3d2a", lineHeight: 1 }}>
                                            {totalSpaceUsed.toFixed(0)}{available > 0 ? `/${available}` : ""}
                                        </div>
                                        <div style={{ fontSize: "0.7rem", color: "#6c757d", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>{spaceUnit} used</div>
                                    </div>
                                    <div style={{ textAlign: "center" }}>
                                        <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#2e3d2a", lineHeight: 1 }}>{totalEstHarvest.toFixed(1)}</div>
                                        <div style={{ fontSize: "0.7rem", color: "#6c757d", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>{harvestUnit} est. harvest</div>
                                    </div>
                                </div>
                                {(dominantFamily || overSpace) && (
                                    <div style={{ padding: "10px 20px", borderTop: "1px solid #eef1eb", fontSize: "0.82rem" }}>
                                        {dominantFamily && (
                                            <div style={{ color: "#9a6a00" }}>
                                                <i className="fas fa-exclamation-triangle" /> Over half your crops are <b>{dominantFamily[0]}</b> — consider more variety for rotation/pest balance.
                                            </div>
                                        )}
                                        {overSpace && (
                                            <div style={{ color: "#C0392B", marginTop: dominantFamily ? 4 : 0 }}>
                                                <i className="fas fa-exclamation-triangle" /> This plan exceeds the area's available space.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* One row per crop with a mini timeline */}
                            {ssPlanned
                                .slice()
                                .sort((a, b) => (parseInt(a.crop.daysToHarvest) || 0) - (parseInt(b.crop.daysToHarvest) || 0))
                                .map((g, i) => {
                                const first = g.entries[0], last = g.entries[g.entries.length - 1];
                                const n = g.entries.length;
                                return (
                                    <div key={i} style={{ padding: "10px 0", borderBottom: "1px solid #eee" }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                                            <div><b>{g.crop.species}</b><span style={{ color: "#6c757d", fontSize: "0.82rem" }}> · {g.family}</span></div>
                                            <span style={{ backgroundColor: "#DDE5D7", borderRadius: 4, padding: "2px 8px", fontSize: "0.78rem", color: "#2e3d2a" }}>
                                                {n > 1 ? `${n} successions` : "1 planting"}
                                            </span>
                                        </div>
                                        <div style={{ background: "#eee", borderRadius: 3, padding: "2px 0" }}>
                                            {g.entries.map((e, j) => {
                                                const sp = pct(e.seedStartDate), mp = pct(e.transplantDate), ep = pct(e.harvestDate);
                                                return (
                                                    <div key={j} style={{ position: "relative", height: 7, marginBottom: j < g.entries.length - 1 ? 2 : 0 }}>
                                                        <div style={{ position: "absolute", left: `${sp}%`, width: `${Math.max(0.5, mp - sp)}%`, top: 0, height: 6, background: "#00b894", borderRadius: 2 }} />
                                                        <div style={{ position: "absolute", left: `${mp}%`, width: `${Math.max(0.5, ep - mp)}%`, top: 0, height: 6, background: "#0984e3", borderRadius: 2 }} />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <div style={{ fontSize: "0.78rem", color: "#6c757d", marginTop: 4 }}>
                                            {formatDate(first.seedStartDate)} – {formatDate(last.harvestDate)}
                                        </div>
                                    </div>
                                );
                            })}

                            <div style={{ marginTop: 20, display: "flex", justifyContent: "space-between" }}>
                                <button type="button" className="btn btn-sm" style={{ backgroundColor: "#fff", borderColor: "#d1d5db", color: "#212529" }} onClick={() => setSsTab(1)}>
                                    <i className="bx bx-left-arrow-alt" /> Back
                                </button>
                                <button type="button" className="btn btn-sm" style={{ backgroundColor: "#FF9900", borderColor: "#FF9900", color: "#fff" }} onClick={ssGoNext}>
                                    Continue <i className="bx bx-right-arrow-alt" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ---- Tab 3: Save Plan ---- */}
                    {ssTab === 3 && (
                        <div style={{ textAlign: "center", padding: "20px" }}>
                            <p className="text-muted">
                                Add <b>{totalPlantings}</b> planting{totalPlantings === 1 ? "" : "s"} to <b>{selectedArea?.name}</b> for <b>{season}</b>, or print a timeline.
                            </p>
                            <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 16 }}>
                                <button type="button" className="btn btn-sm" style={{ backgroundColor: "#fff", borderColor: "#d1d5db", color: "#212529" }} onClick={printPlanTimeline}>
                                    <i className="bx bx-printer" /> Print Timeline
                                </button>
                                <button type="button" className="btn btn-sm" style={{ backgroundColor: "#FF9900", borderColor: "#FF9900", color: "#fff" }} onClick={ssSaveAll}>
                                    Save to {selectedArea?.name}
                                </button>
                            </div>
                            <div style={{ marginTop: 16 }}>
                                <button type="button" className="btn btn-sm" style={{ backgroundColor: "transparent", border: "none", color: "#6c757d", textDecoration: "underline" }} onClick={() => setSsTab(2)}>
                                    Back to review
                                </button>
                            </div>
                        </div>
                    )}
                </>)}
            </CardBody></Card></Col></Row>
            </React.Fragment>
        );
    }

    /* ---- Finish screen ---- */
    if (saved) {
        return (
            <React.Fragment>
                <Row>
                    <Col lg="12">
                        <Card>
                            <CardBody>
                    <div style={{ textAlign: "center", padding: "48px 20px 32px" }}>
                        <div style={{
                            width: 72, height: 72, borderRadius: "50%",
                            backgroundColor: "#DDE5D7", display: "flex",
                            alignItems: "center", justifyContent: "center",
                            margin: "0 auto 20px"
                        }}>
                            <i className="fas fa-check" style={{ fontSize: 32, color: "#4a5e44" }} />
                        </div>
                        <h4 style={{ color: "#2e3d2a", marginBottom: 6 }}>Schedule Saved!</h4>
                        <p className="text-muted mb-4">
                            Your crop rotation has been added to <strong>{selectedArea?.name}</strong>.
                        </p>

                        <div style={{
                            background: "#f5f6f3", borderRadius: 8,
                            padding: "16px 24px", maxWidth: 520,
                            margin: "0 auto 32px", textAlign: "left"
                        }}>
                            <div style={{ fontWeight: 700, fontSize: "1rem", color: "#2e3d2a", marginBottom: 12 }}>
                                {selectedArea?.name}
                            </div>
                            <div style={{ display: "flex", gap: 32, marginBottom: 16 }}>
                                <div>
                                    <div style={{ fontSize: "1.6rem", fontWeight: 700, color: "#4a5e44", lineHeight: 1 }}>{rotations.length}</div>
                                    <div style={{ fontSize: "0.75rem", color: "#6c757d", marginTop: 2 }}>Rotations</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: "1.6rem", fontWeight: 700, color: "#4a5e44", lineHeight: 1 }}>{totalCropCount}</div>
                                    <div style={{ fontSize: "0.75rem", color: "#6c757d", marginTop: 2 }}>Crops</div>
                                </div>
                            </div>

                            {rotations.map((r, i) => (
                                <div key={i} style={{ marginBottom: 12 }}>
                                    <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#7F8F7B", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                                        Rotation {i + 1}
                                    </div>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                        {["spring","summer","fall"].filter(s => r[s]).map(s => (
                                            <span key={s} style={{
                                                backgroundColor: "#DDE5D7", borderRadius: 4,
                                                padding: "3px 8px", fontSize: "0.82rem", color: "#2e3d2a"
                                            }}>
                                                <span style={{ fontWeight: 600, textTransform: "capitalize" }}>{s}</span>: {r[s].species}
                                                {r[s].seedStartDate && <span style={{ color: "#6c757d" }}> · seeds {formatDate(r[s].seedStartDate)}</span>}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
                            <button type="button" className="btn btn-sm"
                                style={{ backgroundColor: "#FF9900", borderColor: "#FF9900", color: "#fff" }}
                                onClick={resetWizard}>
                                Plan Another Rotation
                            </button>
                        </div>
                    </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </React.Fragment>
        );
    }

    /* ---- Arrow component ---- */
    const Arrow = () => (
        <div className="d-flex justify-content-center align-items-center h-100"
            style={{ fontSize: "2.75rem", color: "#adb5bd" }}>
            <i className="bx bx-right-arrow-alt" />
        </div>
    );

    /* ---- Season header component ---- */
    const SeasonHeader = ({ label, icon }) => (
        <div style={{
            textAlign: "center", fontWeight: 600, fontSize: "1rem",
            paddingBottom: "8px", borderBottom: "2px solid #BEC8B6",
            color: "#2e3d2a", display: "flex", alignItems: "center",
            justifyContent: "center", gap: 6
        }}>
            <i className={`fas ${icon}`} style={{ color: "#7F8F7B" }} />
            {label}
        </div>
    );

    /* ---- Main render ---- */
    return (
        <React.Fragment>
        <Row>
          <Col lg="12">
            <Card>
                <CardBody>
                <style>{`
                    .twitter-bs-wizard-nav .nav-link .step-number {
                        background-color: #BEC8B6 !important;
                        color: #3d4a38 !important;
                        border-color: #BEC8B6 !important;
                    }
                    .twitter-bs-wizard-nav .nav-link.active .step-number {
                        background-color: #FF9900 !important;
                        color: #fff !important;
                        border-color: #FF9900 !important;
                    }
                    .twitter-bs-wizard-nav .nav-link.active {
                        color: #212529 !important;
                    }
                    .twitter-bs-wizard-nav,
                    .twitter-bs-wizard-nav .nav-link,
                    .twitter-bs-wizard-nav .nav-link.active {
                        background-color: #DDE5D7 !important;
                    }
                    .twitter-bs-wizard-nav .nav-link.completed .step-number {
                        background-color: #7F8F7B !important;
                        color: #fff !important;
                        border-color: #7F8F7B !important;
                    }
                `}</style>

                {modeToggle}

                <div id="progrss-wizard" className="twitter-bs-wizard">
                    <ul className="twitter-bs-wizard-nav nav-justified nav nav-pills">
                        {[
                            { n: 1, label: "Choose Area & Crops" },
                            { n: 2, label: "Review Rotation Plan" },
                            { n: 3, label: "Save Schedule" },
                        ].map(({ n, label }) => (
                            <NavItem key={n}>
                                <NavLink
                                    className={classnames({
                                        active: activeTabProgress === n,
                                        completed: activeTabProgress > n,
                                    })}
                                    onClick={() => activeTabProgress > n && toggleTabProgress(n)}
                                    style={{ cursor: activeTabProgress > n ? "pointer" : "default" }}
                                >
                                    <span className="step-number mr-2">
                                        {activeTabProgress > n ? <i className="fas fa-check" /> : n}
                                    </span>
                                    <span>{label}</span>
                                </NavLink>
                            </NavItem>
                        ))}
                    </ul>

                    <div id="bar" className="mt-4" style={{ marginBottom: "16px" }}>
                        <div style={{ height: "6px", backgroundColor: "#DDE5D7", borderRadius: 3 }}>
                            <div style={{
                                height: "100%", width: `${progressValue}%`,
                                backgroundColor: "#7F8F7B", borderRadius: 3,
                                transition: "width 0.4s ease"
                            }} />
                        </div>
                    </div>

                    <TabContent activeTab={activeTabProgress} className="twitter-bs-wizard-tab-content">

                        {/* ===================== TAB 1 ===================== */}
                        <TabPane tabId={1}>
                            <div style={{
                                backgroundColor: "#DDE5D7", borderLeft: "4px solid #7F8F7B",
                                borderRadius: "4px", padding: "14px 18px",
                                marginBottom: "20px", marginTop: "12px"
                            }}>
                                <div style={{ fontWeight: 600, fontSize: "1rem", marginBottom: 4, color: "#2e3d2a" }}>
                                    Choose Area and Crops
                                </div>
                                <div style={{ fontSize: "0.9rem", color: "#3d4a38", lineHeight: "1.5" }}>
                                    Select the crops you want to rotate. SAGE will sequence them using Biointensive soil health principles and calculate your planting calendar by seasons.
                                </div>
                            </div>

                            {/* Area select */}
                            <div className="mb-4">
                                <label style={{ fontWeight: 600, color: "#2e3d2a", marginBottom: 6, display: "block" }}>
                                    Area
                                </label>
                                <Input
                                    type="select"
                                    value={selectedAreaId}
                                    onChange={e => { setSelectedAreaId(e.target.value); setErrors(prev => ({ ...prev, area: undefined })); }}
                                    style={{ maxWidth: 320, borderColor: errors.area ? "#dc3545" : undefined }}
                                >
                                    <option value="">— select an area —</option>
                                    {areas
                                        .filter(a => a.name !== "Unassigned" && a.year === currYear)
                                        .map(a => (
                                            <option key={a.id} value={a.id}>{a.name}</option>
                                        ))}
                                </Input>
                                {errors.area && <div className="text-danger mt-1" style={{ fontSize: "0.85rem" }}>{errors.area}</div>}
                            </div>

                            {/* Search + add */}
                            <div className="mb-1">
                                <label style={{ fontWeight: 600, color: "#2e3d2a", marginBottom: 6, display: "block" }}>
                                    Add crops
                                </label>
                                <div style={{ display: "flex", alignItems: "flex-start", gap: 8, maxWidth: 520 }}>
                                    <div style={{ flex: 1 }}>
                                        <PlantSelect
                                            key={searchKey}
                                            plantOptions={plantOptions}
                                            value={null}
                                            onChange={addPlantFromSearch}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-sm"
                                        style={{
                                            backgroundColor: pendingCrop ? "#FF9900" : "#BEC8B6",
                                            borderColor: pendingCrop ? "#FF9900" : "#BEC8B6",
                                            color: "#fff",
                                            whiteSpace: "nowrap",
                                            opacity: pendingCrop ? 1 : 0.6,
                                        }}
                                        onClick={confirmAddCrop}
                                        disabled={!pendingCrop}
                                    >
                                        + Add{pendingCrop ? ` "${pendingCrop.species}"` : " crop"}
                                    </button>
                                </div>
                                {pendingCrop && (
                                    <div style={{ marginTop: 6, fontSize: "0.82rem", color: "#6c757d" }}>
                                        <i className="fas fa-info-circle" style={{ marginRight: 4 }} />
                                        {getWeatherType(pendingCrop) === "warm" ? "Warm season" : getWeatherType(pendingCrop) === "cool" ? "Cool season" : ""}
                                        {pendingCrop.family ? ` · ${pendingCrop.family}` : ""}
                                        {pendingCrop.daysToHarvest ? ` · ${pendingCrop.daysToHarvest} days to harvest` : ""}
                                    </div>
                                )}
                                {errors.plants && (
                                    <div className="text-danger mt-1" style={{ fontSize: "0.85rem" }}>{errors.plants}</div>
                                )}
                            </div>

                            {/* Selected crops list */}
                            <div style={{ marginTop: 16 }}>
                                {plants.map((plant, idx) => (
                                    <div
                                        key={idx}
                                        style={{
                                            display: "flex", alignItems: "center", gap: 12,
                                            padding: "10px 12px", marginBottom: 8,
                                            border: "1px solid #dde5d7", borderRadius: 6,
                                            backgroundColor: "#fff",
                                        }}
                                    >
                                        <div style={{ flexShrink: 0, width: 100, height: 64, borderRadius: 4, overflow: "hidden", border: "1px solid #dde5d7", display: "flex", alignItems: "center", backgroundColor: "#f5f6f3" }}>
                                            <StorageImage
                                                alt={plant.species}
                                                imgKey={plant.cardIconKey
                                                    ? plant.cardIconKey
                                                    : "card_icons/" + plant.species.toLowerCase().replace(/ /g, "_").replace(/[(),]/g, "") + ".png"}
                                                accessLevel="guest"
                                                onStorageGetError={() => {}}
                                                style={{ width: "100px", height: "64px", objectFit: "cover", display: "block" }}
                                            />
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: 600, fontSize: "0.95rem", color: "#2e3d2a", marginBottom: 2 }}>
                                                {plant.species}
                                            </div>
                                            <div style={{ fontSize: "0.8rem", color: "#6c757d" }}>
                                                {plant.family && <span style={{ marginRight: 12 }}><b>Family:</b> {plant.family}</span>}
                                                {plant.daysToHarvest && <span style={{ marginRight: 12 }}><b>Days to harvest:</b> {plant.daysToHarvest}</span>}
                                                {getWeatherType(plant) && (
                                                    <span style={{
                                                        display: "inline-block", fontSize: "0.72rem", fontWeight: 600,
                                                        backgroundColor: getWeatherType(plant) === "warm" ? "#f5d9a0" : "#c8dfc0",
                                                        borderRadius: 3, padding: "1px 6px", color: "#2e3d2a"
                                                    }}>
                                                        {getWeatherType(plant) === "warm" ? "Warm season" : "Cool season"}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => removePlant(idx)}
                                            style={{ flexShrink: 0, background: "none", border: "none", cursor: "pointer", color: "#adb5bd", padding: "4px" }}
                                            title="Remove"
                                        >
                                            <i className="bx bx-x" style={{ fontSize: 20 }} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </TabPane>

                        {/* ===================== TAB 2 ===================== */}
                        <TabPane tabId={2}>
                            <div style={{
                                backgroundColor: "#DDE5D7", borderLeft: "4px solid #7F8F7B",
                                borderRadius: "4px", padding: "14px 18px",
                                marginBottom: "20px", marginTop: "12px"
                            }}>
                                <div style={{ fontWeight: 600, fontSize: "1rem", marginBottom: 4, color: "#2e3d2a" }}>
                                    Review Rotation Plan
                                </div>
                                <div style={{ fontSize: "0.9rem", color: "#3d4a38", lineHeight: "1.5" }}>
                                    SAGE has sequenced your crops by season using nutrient balance and family diversity principles. Each row is one rotation to follow year after year.
                                </div>
                            </div>

                            <Row className="mb-3 align-items-end">
                                <Col md={3}><SeasonHeader label="Spring" icon="fa-seedling" /></Col>
                                <Col md={1} />
                                <Col md={3}><SeasonHeader label="Summer" icon="fa-sun" /></Col>
                                <Col md={1} />
                                <Col md={3}><SeasonHeader label="Fall" icon="fa-leaf" /></Col>
                                <Col md={1} />
                            </Row>

                            {rotations.map((rotation, idx) => (
                                <Row key={idx} className="align-items-start mb-4">
                                    <Col md={3}>
                                        {rotation.spring && (
                                            <PlantPreviewCard plant={rotation.spring} unitmeasurement={profile?.unitmeasurement} mode="rotation" />
                                        )}
                                    </Col>
                                    <Col md={1}><Arrow /></Col>
                                    <Col md={3}>
                                        {rotation.summer && (
                                            <PlantPreviewCard plant={rotation.summer} unitmeasurement={profile?.unitmeasurement} mode="rotation" />
                                        )}
                                    </Col>
                                    <Col md={1}><Arrow /></Col>
                                    <Col md={3}>
                                        {rotation.fall && (
                                            <PlantPreviewCard plant={rotation.fall} unitmeasurement={profile?.unitmeasurement} mode="rotation" />
                                        )}
                                    </Col>
                                    <Col md={1} />
                                </Row>
                            ))}
                        </TabPane>

                        {/* ===================== TAB 3 ===================== */}
                        <TabPane tabId={3}>
                            <div style={{
                                backgroundColor: "#DDE5D7", borderLeft: "4px solid #7F8F7B",
                                borderRadius: "4px", padding: "14px 18px",
                                marginBottom: "20px", marginTop: "12px"
                            }}>
                                <div style={{ fontWeight: 600, fontSize: "1rem", marginBottom: 4, color: "#2e3d2a" }}>
                                    Save Schedule
                                </div>
                                <div style={{ fontSize: "0.9rem", color: "#3d4a38", lineHeight: "1.5" }}>
                                    Review your complete rotation plan below, then save it to your area. Your planting calendar will be calculated based on your climate data.
                                </div>
                            </div>

                            {/* Summary card */}
                            <div style={{
                                border: "1px solid #BEC8B6", borderRadius: 8,
                                padding: "20px 24px", marginBottom: 28,
                                backgroundColor: "#f9faf8"
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                                    <i className="bx bx-map-pin" style={{ fontSize: 20, color: "#7F8F7B" }} />
                                    <span style={{ fontWeight: 700, fontSize: "1.05rem", color: "#2e3d2a" }}>
                                        {selectedArea?.name}
                                    </span>
                                    <span style={{
                                        backgroundColor: "#DDE5D7", borderRadius: 12,
                                        padding: "2px 10px", fontSize: "0.8rem", color: "#4a5e44", marginLeft: 4
                                    }}>
                                        {totalCropCount} crops · {rotations.length} rotation{rotations.length !== 1 ? "s" : ""}
                                    </span>
                                </div>

                                {rotations.map((r, i) => (
                                    <div key={i} style={{
                                        marginBottom: 16, paddingBottom: 16,
                                        borderBottom: i < rotations.length - 1 ? "1px solid #DDE5D7" : "none"
                                    }}>
                                        <div style={{
                                            fontSize: "0.78rem", fontWeight: 600, color: "#7F8F7B",
                                            textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8
                                        }}>
                                            Rotation {i + 1}
                                        </div>
                                        <Row>
                                            {["spring","summer","fall"].map(season => r[season] && (
                                                <Col key={season} md={4}>
                                                    <div style={{ marginBottom: 4 }}>
                                                        <span style={{
                                                            display: "inline-block", fontSize: "0.72rem",
                                                            fontWeight: 600, textTransform: "capitalize",
                                                            backgroundColor: season === "spring" ? "#c8dfc0"
                                                                : season === "summer" ? "#f5d9a0"
                                                                : "#d4c9b8",
                                                            borderRadius: 3, padding: "1px 6px",
                                                            marginBottom: 3, color: "#2e3d2a"
                                                        }}>
                                                            {season}
                                                        </span>
                                                        <div style={{ fontWeight: 600, color: "#2e3d2a", fontSize: "0.9rem" }}>
                                                            {r[season].species}
                                                        </div>
                                                        <div style={{ fontSize: "0.8rem", color: "#6c757d" }}>
                                                            Seeds: {formatDate(r[season].seedStartDate)}
                                                        </div>
                                                        <div style={{ fontSize: "0.8rem", color: "#6c757d" }}>
                                                            Harvest: {formatDate(r[season].harvestDate)}
                                                        </div>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                ))}
                            </div>
                        </TabPane>
                    </TabContent>

                    {/* ---- Footer buttons ---- */}
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24, paddingTop: 16, borderTop: "1px solid #DDE5D7" }}>
                        <div>
                            {activeTabProgress > 1 && (
                                <button type="button" className="btn btn-sm"
                                    style={{ backgroundColor: "#fff", borderColor: "#d1d5db", color: "#212529" }}
                                    onClick={() => toggleTabProgress(activeTabProgress - 1)}>
                                    ← Previous
                                </button>
                            )}
                        </div>
                        <div>
                            {activeTabProgress < 3 && (
                                <button type="button" className="btn btn-sm"
                                    style={{ backgroundColor: "#FF9900", borderColor: "#FF9900", color: "#fff" }}
                                    onClick={goNext}>
                                    Next →
                                </button>
                            )}
                            {activeTabProgress === 3 && (
                                <button type="button" className="btn btn-sm"
                                    style={{ backgroundColor: "#FF9900", borderColor: "#FF9900", color: "#fff" }}
                                    onClick={handleSaveAll}>
                                    Save to {selectedArea?.name || "Area"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                </CardBody>
            </Card>
          </Col>
        </Row>
        </React.Fragment>
    );
};

/* ---- Redux ---- */
const mapStateToProps = (state) => {
    const sorted = [...state.designer.plants].sort((a, b) => a.name.localeCompare(b.name));
    return {
        plantData: sorted,
        plantMap: sorted.reduce((m, p) => { m[p.name] = p; return m; }, {}),
        climateData: state.designer.temps,
        profile: state.Profile.user
    };
};

const mapDispatchToProps = (dispatch) => ({
    onGetPlantData: () => dispatch(getPlantData()),
    onGetProfile: (u) => dispatch(getProfile(u)),
    onGetClimateData: (z) => dispatch(getClimateData(z))
});

export default connect(mapStateToProps, mapDispatchToProps)(CropPlanner);
